import { useState, type JSX, type ReactNode } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { CopyIcon, CheckIcon } from './icons.js';
import { MermaidDiagram } from './MermaidDiagram.js';

/**
 * ChatGPT-style code block override for react-markdown's `code` component.
 * Inline code (no language fence) renders as a small mono pill; fenced
 * blocks with `language-*` classes render through Prism (one-dark) with
 * a header showing the language label + a Copy button.
 *
 * Used by both the Knowledge Agent answer body and the chunk-inspector
 * modals so SQL / TS / etc. blocks look the same wherever they appear.
 */
export function CodeBlock(props: {
  className?: string;
  children?: ReactNode;
  inline?: boolean;
}): JSX.Element {
  const { inline, className, children } = props;
  const match = /language-(\w+)/.exec(className ?? '');
  const code = String(children ?? '').replace(/\n$/, '');
  if (inline || !match) {
    return (
      <code className="rounded bg-ink-900/10 px-1 py-0.5 text-[0.85em] font-mono">{children}</code>
    );
  }
  // A ```mermaid fence renders as an editable diagram (draw.io export + SVG),
  // not syntax-highlighted source. Works anywhere CodeBlock is used — chat
  // answers and the canvas.
  if (match[1] === 'mermaid') {
    return <MermaidDiagram code={code} />;
  }
  return <CodeFenceCard code={code} lang={match[1] ?? 'code'} />;
}

function CodeFenceCard({ code, lang }: { code: string; lang: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked — silent fail */
    }
  };
  // Empty / whitespace-only fence: the deep-mode prompts tell the LLM to
  // include code blocks only when a fragment contains real code, but it
  // occasionally still emits a bare ```sql\n``` placeholder. Render an
  // explicit notice instead of an invisible black bar so the user can
  // tell the fence is empty (not a rendering bug).
  const trimmed = code.trim();
  return (
    <div className="my-3 overflow-hidden rounded-lg border border-ink-900/10 bg-[#282c34]">
      <div className="flex items-center justify-between border-b border-black/30 bg-black/30 px-3 py-1.5 text-[11px] font-medium text-white/70">
        <span className="font-mono uppercase tracking-wider">{lang || 'code'}</span>
        <button
          type="button"
          onClick={onCopy}
          disabled={!trimmed}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-transparent"
        >
          {copied ? <CheckIcon size={12} /> : <CopyIcon size={12} />}
          <span>{copied ? 'Copied' : 'Copy'}</span>
        </button>
      </div>
      {trimmed ? (
        <SyntaxHighlighter
          language={lang}
          style={oneDark}
          customStyle={{
            margin: 0,
            padding: '0.75rem 1rem',
            background: 'transparent',
            fontSize: '0.85em',
            color: '#abb2bf',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
          codeTagProps={{ style: { color: '#abb2bf', background: 'transparent' } }}
          PreTag="div"
        >
          {code}
        </SyntaxHighlighter>
      ) : (
        <div className="px-4 py-3 font-mono text-[12px] italic text-white/40">
          (no code snippet in the matching fragment)
        </div>
      )}
    </div>
  );
}
